import React, { useCallback, useEffect, useState } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridComparatorFn,
  GridOverlay,
  GridSortModel,
  GridFilterModel,
  getGridDateOperators,
} from '@mui/x-data-grid-pro';
import { Card, MenuItem, Link as MuiLink, Select, SelectChangeEvent } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import Button from '@mui/material/Button';

import { getAssessmentSurveyAssessmentsForDataGrid } from '../../services/assessmentSurveyAssessment';
import { AssessmentSurveyAssessment } from '../../interfaces/assessmentSurveyAssessments.interface';
import { fetchAssessment, updateAssessmentSurveySimple, updateAssessmentSingleKey, processAssessmentsReportRequest } from '../../services/assessments';
import moment from 'moment';
import { getCompany, updateCompanyBoolean } from '../../services/company';
import { Survey } from '../../interfaces/survey.interface';
import { updateContactShort } from '../../services/contact';
import { updateCompanyApplicationShort } from '../../services/companyApplication';
import { Link } from 'react-router-dom';
import { Assessment, AssessmentSurvey } from '../../interfaces/assessment.interface';
import User, { Version, AssessorLevel } from '../../interfaces/users.interface';
import { getUsersByType } from '../../app/store/usersSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import CircularProgress from '@mui/material/CircularProgress';
import { createBadge, updateVersionBadge } from '../../services/applications';
import { fetchWorkflows, isWorkflow531OrHigher, updateWorkflow } from '../../services/workflow';
import { USER_TYPES } from '../../services/user';
import BaseToolbar from '../Datagrid/BaseToolbar';
import { GOLD_BADGE_LABEL, GOLD_NEXT_GEN_BADGE_LABEL, GOLD_STAR_BADGE_LABEL, SILVER_BADGE_LABEL, eligibleForGoldStarBadge, eligibleForNextGenGoldBadge, processVerificationAward } from '../../services/badge';
import { Workflow } from '../../interfaces/workflow.interface';
import { normalizeFilterModel, sameModel, stringContainsOnly } from '../../utils/adminDashboardUtils';
import ReportRequestButton from './ReportRequestButton';
import { AsyncSaveButton } from '../../components/buttons/AsyncSaveButton';

let assessorUsers:User[] = [];

interface AssessmentRow {
  id: number;
  vendorName: string;
  assessorCompanyName: string;
  assessorName: string;
  assessorLevel: null | undefined | AssessorLevel | string;
  siteOrApplicationName: string;
  link: string;
  approve: string;
  fullObject: AssessmentSurveyAssessment;
  adminReviewer: number;
  submittedDate: string;
  assessmentSurvey: AssessmentSurvey;
  assessment: Assessment;
  dateAccepted: string | undefined;
  reviewStatus?: string;
}

function CustomToolbar(props: { showCompletedAssessmentSurveys: boolean; toggleShowCompletedAssessmentSurveys: () => void }) {
  const { showCompletedAssessmentSurveys, toggleShowCompletedAssessmentSurveys, ...rest } = props;

  const handleProcessAssessmentsReportRequest = useCallback(async () => {
    try {
      await processAssessmentsReportRequest();
    } catch (error) {
      console.error('Failed to process assessments report request', error);
      throw error;
    }
  }, []);

  return (
    <BaseToolbar
    {...rest}
    exportExtraItems={
      <ReportRequestButton
        type="Assessments"
        onRequest={handleProcessAssessmentsReportRequest}
      />
    }
    csvOptions={{ fileName: `tpn_in_review_assessments_${new Date().toLocaleDateString()}` }}
  >
    <Button variant="text" size="small" onClick={toggleShowCompletedAssessmentSurveys}>
      Show {showCompletedAssessmentSurveys ? 'Submitted' : 'Completed'} Assessments
      </Button>
    </BaseToolbar>
  );
}

const isEmail = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;

function createRowLink(assRow: AssessmentRow) {
  return (<>{assRow.link !== '-' &&
    <Link to={assRow.link}>
      <IconButton size='small' name='View Certification'>
        <LinkOutlinedIcon />
      </IconButton>
    </Link>
  }</>);
}

function selectAdmin(assRow: AssessmentRow) {
  const handleSetReviewer = (event: SelectChangeEvent) => {
    if (!event || !event.target.value) return;
    const user = event.target.value;
    updateAssessmentSurveySimple(assRow.id, { adminReviewer: user });
  };

  return (<>
    { !assessorUsers && assRow.adminReviewer }
    { assessorUsers &&
      <Select
        labelId="company-primary-contact"
        id="company-primary-contact-select"
        label="Primary Contact"
        onChange={handleSetReviewer}
        defaultValue={assRow.adminReviewer?.toString()}
        fullWidth
      >
        {assessorUsers.filter(e => e.firstName?.length > 0 || e.lastName?.length > 0).map((admin: User) =>
          <MenuItem key={admin.id} value={admin.id} selected={true}>{admin.firstName} {admin.lastName}</MenuItem>,
        )}
      </Select>
    }
  </>);
}

const dateComparator: GridComparatorFn<Date> = (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime();

function createAssessmentRow(assSurAss: AssessmentSurveyAssessment, assessmentSurvey: AssessmentSurvey, assessment: Assessment) {
  const approve = 'Approve';
  if (assSurAss.assessments.length === 0) return {
    id: assSurAss.id,
    vendorName: '',
    assessorCompanyName: '',
    assessorName: '',
    assessorLevel: null,
    siteOrApplicationName: '',
    link: '-',
    approve,
    fullObject: assSurAss,
    adminReviewer: assSurAss.adminReviewer as number,
    submittedDate: assSurAss.submittedDate as string,
    dateAccepted: assessment.dateAccepted,
    assessmentSurvey,
    assessment,
  };
  const link = `/assessment/${assSurAss.assessments[0].id}/questionnaire/${assSurAss.survey}`;

  const survey = assSurAss.survey;
  const vendor = assSurAss.assessments[0].company;
  const assessorCompany = assSurAss.assessments[0].assessor;
  const assessorName = assSurAss.assessments[0].assignedAssessor?.firstName ? assSurAss.assessments[0].assignedAssessor.firstName + ' ' + assSurAss.assessments[0].assignedAssessor.lastName : assSurAss.assessments[0].assignedAssessor?.email;
  let siteApp = '';

  if (!assSurAss.submittedDate) {
    updateAssessmentSurveySimple(assSurAss.id, { submittedDate: assSurAss.updatedAt });
    assSurAss.submittedDate = assSurAss.updatedAt;
  }

  if (vendor.companyApplications && vendor.companyApplications.length > 0) {
    vendor.companyApplications.forEach(CA => {
      if (siteApp.length === 0 && CA.application.surveys.indexOf(survey) !== -1) siteApp = CA.application.name;
    });
  }

  if (siteApp.length === 0 && vendor.locations && vendor.locations.length > 0) {
    vendor.locations.forEach(loc => {
      if (siteApp.length === 0 && loc.surveys.indexOf(survey) !== -1) siteApp = loc.name;
    });
  }

  return {
    id: assSurAss.id,
    vendorName: vendor.name,
    assessorCompanyName: assessorCompany?.name,
    assessorName,
    assessorLevel: assessorCompany?.assessorLevel ? assessorCompany?.assessorLevel.descriptor : 'Null',
    siteOrApplicationName: siteApp,
    link,
    approve,
    fullObject: assSurAss,
    adminReviewer: assSurAss.adminReviewer as number,
    submittedDate: assSurAss.submittedDate ? moment(assSurAss.submittedDate).format('MM/DD/YYYY') : '-',
    dateAccepted: assessment.dateAccepted ? moment(assessment.dateAccepted).format('MM/DD/YYYY') : '-',
    assessmentSurvey,
    assessment,
    reviewStatus: assessmentSurvey.reviewStatus,
  };
}

export default function AssessmentAdminPanel(props: { assessmentsAddressed: any }) {
  const dispatch = useAppDispatch();
  assessorUsers = useAppSelector(state => state.users).results || [];
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  const [rows, setRows] = useState<Array<AssessmentRow>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingRowId, setSavingRowId] = useState<number | null>(null);

  function createApproveButton(assRow: AssessmentRow) {
    const theRow = { ...assRow };

    const awardBadgesIfNoRemediations = async (
      workflow: Workflow,
      newDate: moment.Moment,
      status: string,
      versionId?: number,
    ) => {
      const isWf531OrGreater = isWorkflow531OrHigher(workflow.workflowType);
      const badgeIdSet = new Set<number>(workflow.badges ? workflow.badges.map(b => typeof b === 'number' ? b : b.id!) : []);

      try {
        if (isWf531OrGreater) {
          // Award Silver, NextGen Gold and Gold Plus
          const silverBadge = await createBadge({
            title: SILVER_BADGE_LABEL,
            awardedDate: newDate.format('YYYY-MM-DD'),
            expirationDate: moment(newDate.format('YYYY-MM-DD')).add(2, 'years').format('YYYY-MM-DD'),
          });
          badgeIdSet.add(silverBadge.id!);

          const nextGenEligible = await eligibleForNextGenGoldBadge(workflow.id);
          if (nextGenEligible) {
            const nextGenBadge = await processVerificationAward(GOLD_NEXT_GEN_BADGE_LABEL, workflow.id);
            if (nextGenBadge) badgeIdSet.add(nextGenBadge.id!);
          }

          const goldStarEligible = await eligibleForGoldStarBadge(workflow.id);
          if (goldStarEligible) {
            const goldStarBadge = await processVerificationAward(GOLD_STAR_BADGE_LABEL, workflow.id);
            if (goldStarBadge) badgeIdSet.add(goldStarBadge.id!);
            if (versionId && goldStarBadge) await updateVersionBadge(versionId, goldStarBadge.id!);
          }
        } else {
          // Award Classic Gold
          const classicGoldBadge = await createBadge({
            title: GOLD_BADGE_LABEL,
            awardedDate: newDate.format('YYYY-MM-DD'),
            expirationDate: moment(newDate.format('YYYY-MM-DD')).add(2, 'years').format('YYYY-MM-DD'),
          });
          badgeIdSet.add(classicGoldBadge.id!);
          if (versionId) await updateVersionBadge(versionId, classicGoldBadge.id!);
        }
      } catch (error) {
        console.error('Error processing badges:', error);
      } finally {
        updateWorkflow({ id: workflow.id, badges: [...Array.from(badgeIdSet)], reportIssueDate: newDate.format('YYYY-MM-DD'), status });
      }
    };

    const approveAssessment = async () => {
      const { assessment, assessmentSurvey } = theRow;
      let status = assessmentSurvey.bestPracticeRemediationCount > 0 || assessmentSurvey.implementationGuidanceRemediationCount > 0 ? 'Remediation' : 'Complete';
      setSavingRowId(theRow.id);
      const company = await getCompany(theRow.fullObject.assessments[0].company.id.toString());
      const workflow = await fetchWorkflows({ assessment_survey: assessmentSurvey.id }, true);
      const newDate = moment().utc();
      updateAssessmentSurveySimple(assessmentSurvey.id!, { status, reportIssueDate: newDate.format('YYYY-MM-DDTHH:mm:ssZZ'), expirationDate: newDate.add(2, 'y').format('YYYY-MM-DDTHH:mm:ssZZ') })
        .then(() => {
          setSavingRowId(null);
          setRows(prev =>
            prev.map(row =>
              row.id === theRow.id
                ? {
                  ...row,
                  fullObject: {
                    ...row.fullObject,
                    status,
                  },
                }
                : row,
            ),
          );
        });

      let assessmentStatus = status;

      (assessment.surveys as AssessmentSurvey[]).forEach(assSur => {
        if (assessmentSurvey.id === assSur.id || ['Remediation', 'Complete'].indexOf(assessmentStatus) === -1) return;
        if (['Remediation', 'Complete'].indexOf(assSur.status) === -1) {
          assessmentStatus = assSur.status;
          return;
        }
        if (['Remediation', 'Complete'].indexOf(assSur.status) !== -1) {
          if (assSur.status === 'Remediation' && assessmentStatus === 'Complete') {
            assessmentStatus = 'Remediation';
            return;
          }
        }
      });

      if (['Remediation', 'Complete'].indexOf(assessmentStatus) !== -1) updateAssessmentSingleKey(assessment.id!, 'status', assessmentStatus);
      if (assessmentStatus === 'Remediation') return updateWorkflow({ id: workflow[0].id, reportIssueDate: newDate.format('YYYY-MM-DD'), status: assessmentStatus });
      newDate.add(-2, 'y');
      if (theRow.fullObject.assessments?.length > 0 === false) return updateWorkflow({ id: workflow[0].id, reportIssueDate: newDate.format('YYYY-MM-DD'), status: assessmentStatus });
      updateCompanyBoolean({ id: company.id, displayShieldType: 'tpn_assessed', goldShieldDate: newDate.format('YYYY-MM-DD') });
      if ((assessmentSurvey.survey as Survey).site) {
        updateContactShort({ id: (assessmentSurvey.survey as Survey).site, badge: 'tpn_assessed', goldShieldDate: newDate.format('YYYY-MM-DD') });
        await awardBadgesIfNoRemediations(workflow[0], newDate, assessmentStatus);
      }
      if ((assessmentSurvey.survey as Survey).application) {
        const companyApplication = company.companyApplications?.find(ca => ca.application.id === (assessmentSurvey.survey as Survey).application);
        if (!companyApplication) return;

        const version = (companyApplication.application.versions as Version[]).find(v => v.value === (assessmentSurvey.survey as Survey).version);
        const date = moment().utc();
        if (version) {
          await awardBadgesIfNoRemediations(workflow[0], newDate, assessmentStatus, version.id);
        }

        if (companyApplication.badge !== 'tpn_assessed') updateCompanyApplicationShort({ id: companyApplication.id, badge: 'tpn_assessed', goldShieldDate: date });
      }
    };

    const isSavingThisRow = savingRowId === theRow.id;

    return (<>
      {['Remediation', 'Complete', 'Completed'].indexOf(theRow.fullObject.status) === -1 && (
      <AsyncSaveButton
        variant="outlined"
        color="primary"
        onClick={approveAssessment}
        isSaving={isSavingThisRow}
        savingLabel="Saving..."
        label={theRow.approve}
        />
      )}
      {['Remediation', 'Complete', 'Completed'].indexOf(theRow.fullObject.status) > -1 && 'Approved'}
    </>);
  }

  const reviewStatus = (assRow: AssessmentRow) => {
    const handleSetReviewer = (event: SelectChangeEvent) => {
      if (!event || !event.target.value) return;
      const status = event.target.value;
      updateAssessmentSurveySimple(assRow.id, { reviewStatus: status });
      setRows(rows.map(row => row.id === assRow.id ? { ...row, reviewStatus: status } : row));
    };

    return (
      <Select
        labelId="review-status"
        id="review-status-select"
        label="Reivew Status"
        onChange={handleSetReviewer}
        defaultValue={assRow.reviewStatus}
        fullWidth
      >
        <MenuItem value='reviewing'>TPN Reviewing</MenuItem>
        <MenuItem value='pending_assessor'>Pending Assessor</MenuItem>
        <MenuItem value='updates_complete'>Updates Complete</MenuItem>
      </Select>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      filterOperators: stringContainsOnly,
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'vendorName',
      filterOperators: stringContainsOnly,
      headerName: 'Vendor',
      width: 300,
    },
    {
      field: 'siteOrApplicationName',
      filterOperators: stringContainsOnly,
      headerName: 'Site or Application Name',
      width: 300,
    },
    {
      field: 'assessorCompanyName',
      filterOperators: stringContainsOnly,
      headerName: 'Assessor Company',
      width: 300,
    },
    {
      field: 'assessorName',
      filterOperators: stringContainsOnly,
      headerName: 'Assessor',
      width: 300,
      renderCell: (params) => !isEmail.test(params.row.assessorName) ? params.row.assessorName : <MuiLink href={`mailto:${params.row.assessorName}`}>{params.row.assessorName}</MuiLink>,
    },
    {
      field: 'assessorLevel',
      filterOperators: stringContainsOnly,
      headerName: 'Assessor Level',
      width: 250,
    },
    {
      field: 'dateAccepted',
      headerName: 'Date Accepted',
      width: 150,
      type: 'dateTime',
      filterOperators: getGridDateOperators().filter(op => op.value === 'is'),
      valueGetter: (params) => (params.value ? new Date(params.value) : null),
      valueFormatter: (params) =>
        params.value ? moment(params.value as Date).format('MM/DD/YYYY') : '',
    },
    {
      field: 'submittedDate',
      headerName: 'Date Submitted',
      width: 150,
      type: 'dateTime',
      filterOperators: getGridDateOperators().filter(op => op.value === 'is'),
      sortComparator: dateComparator,
      valueFormatter: params => moment(new Date(params?.value)).format('MM/DD/YYYY'),
    },
    {
      field: 'adminReviewer',
      headerName: 'Assigned Reviewer',
      sortable: false,
      filterable: false,
      width: 300,
      renderCell: params => selectAdmin(params.row), valueFormatter: ({ value }) => {
        if (!value) return;
        const admin = assessorUsers.find(u => u.id === value);
        if (!admin) return;
        return `${admin.firstName} ${admin.lastName}`;
      },
    },
    {
      field: 'link',
      headerName: 'Assessment Link',
      sortable: false,
      filterable: false,
      width: 150,
      renderCell: params => createRowLink(params.row),
    },
    {
      field: 'approve',
      headerName: 'Action',
      filterable: false,
      sortable: false,
      width: 200,
      renderCell: params => createApproveButton(params.row),
      disableExport: true,
    },
    {
      field: 'reviewStatus',
      headerName: 'Review Status',
      width: 300,
      renderCell: params => reviewStatus(params.row),
      filterable: false,
      sortable: false,
      valueFormatter: ({ value }) => {
        if (value === 'reviewing') return 'TPN Reviewing';
        if (value === 'pending_assessor') return 'Pending Assessor';
        if (value === 'updates_complete') return 'Updates Complete';
        return;
      },
    },
  ];

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModelDraft, setFilterModelDraft] = useState<GridFilterModel>({ items: [] });
  const [filterModelApplied, setFilterModelApplied] = useState<GridFilterModel>({ items: [] });
  const [showCompletedAssessmentSurveys, setShowCompletedAssessmentSurveys] = useState(false);

  const toggleShowCompletedAssessmentSurveys = () => {
    setPage(0);
    setShowCompletedAssessmentSurveys(v => !v);
  };

  const fetchAndSetRows = useCallback(async () => {
    try {
      setIsLoading(true);

      const statuses = showCompletedAssessmentSurveys
        ? ['Complete', 'Remediation']
        : ['Submitted'];

      const resp = await getAssessmentSurveyAssessmentsForDataGrid({
        statuses,
        page,
        pageSize,
        sortModel,
        filterModel: filterModelApplied,
      });

      const { results: assessmentSurveyAssessments, count } = resp;

      setRowCount(count);

      const assessmentIds = assessmentSurveyAssessments
        .filter(asa => asa.assessments?.length > 0)
        .map(asa => asa.assessments[0].id);

      const assessments = await Promise.all(
        assessmentIds.map((id) => fetchAssessment(id)),
      );

      const rowsForPage: AssessmentRow[] = assessmentSurveyAssessments
        .filter(assSurAss => assSurAss.assessments?.length > 0)
        .map((assSurAss: AssessmentSurveyAssessment) => {
          const assessment = assessments.find(as => as.id === assSurAss.assessments[0].id);
          const assessmentSurvey = (assessment!.surveys as AssessmentSurvey[]).find(as => as.id === assSurAss.id);
          return createAssessmentRow(assSurAss, assessmentSurvey!, assessment!);
        });

      setRows(rowsForPage);

      props.assessmentsAddressed();
    } catch (error) {
      console.error('Error fetching assessment survey assessments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, sortModel, filterModelApplied, showCompletedAssessmentSurveys]);

  useEffect(() => {
    dispatch(getUsersByType(USER_TYPES.TPN_ADMIN));
  }, []);

  useEffect(() => {
    fetchAndSetRows();
  }, [fetchAndSetRows]);

  return (
    <Card sx={{ padding: '2em' }}>
      <DataGridPro
        style={{ height: 400, width: '100%' }}
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        loading={isLoading}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setPage(0);
          setSortModel(model);
        }}
        filterMode="server"
        filterModel={filterModelDraft}
        onFilterModelChange={(model) => {
          setPage(0);
          setFilterModelDraft(model);
          const normalized = normalizeFilterModel(model);
          if (!sameModel(normalized, filterModelApplied)) {
            setFilterModelApplied(normalized);
          }
        }}
        components={{
          Toolbar: CustomToolbar,
          LoadingOverlay: () => (
            <GridOverlay>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress />
              </div>
            </GridOverlay>
          ),
        }}
        componentsProps={{
          toolbar: {
            showCompletedAssessmentSurveys,
            toggleShowCompletedAssessmentSurveys,
            csvOptions: { fileName: `tpn_in_review_assessments_${new Date().toLocaleDateString()}` },
          },
        }}
      />
    </Card>
  );
}